import { DateTime } from '@ksyos/date-time';
import { useEffect, useState } from 'react';
import type { CompensationPayoutForServiceActivityWithId } from '@common/validation/compensationPayoutForServiceActivity';
import { ManagerAuthorization } from '@common/enums';
import { getLanguage } from '@common/translate/translate';
import {
    deleteServiceActivityCompensationPayouts,
    getServiceActivityCompensationPayouts,
} from '@frontend/services/api/compensationPayoutApiService';
import { getServiceActivities } from '@frontend/services/api/servicesApiService';
import { hasOneAuthorization } from '@frontend/utils';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import type { ServiceActivity } from '../personalSettings/addRights/utils/serviceEmploymentSettingsUtils';
import {
    AdminTable,
    DeleteButton,
    EditButton,
    FormHeader,
    LinkButton,
    Loading,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableRow,
} from '../../elements/common';
import { FormatCurrency } from '../../elements/FormatCurrency';
import { getUrlWithParams } from './utils/getServiceActivityCompensationPayoutUrl';

interface ServiceActivityCompensationPayoutListProps {
    groupId?: string;
    organizationId?: string;
    serviceGroupSettingId?: string;
    serviceId?: string;
    serviceOrganizationSettingId?: string;
}

export function ServiceActivityCompensationPayoutList(props: ServiceActivityCompensationPayoutListProps) {
    const { groupId, organizationId, serviceId, serviceGroupSettingId, serviceOrganizationSettingId } = props;
    const translate = getLanguage();
    const canEdit =
        (props.serviceOrganizationSettingId &&
            hasOneAuthorization([
                ManagerAuthorization.organizationAddEdit,
                ManagerAuthorization.serviceRightsOrganizations,
            ])) ||
        (props.serviceGroupSettingId &&
            hasOneAuthorization([ManagerAuthorization.groupAddEdit, ManagerAuthorization.serviceRightsGroups]));

    const abortSignal = useAbortSignal();
    const [compensationPayouts, setCompensationPayouts] = useState<CompensationPayoutForServiceActivityWithId[]>([]);
    const [serviceActivities, setServiceActivities] = useState<ServiceActivity[]>([]);

    useEffect(() => {
        load();
    }, []);

    let actionButtons: JSX.Element[] | undefined;
    if (canEdit) {
        actionButtons = [
            <LinkButton
                icon="add"
                key="add"
                to={getUrlWithParams('/serviceActivityCompensationPayout/add', {
                    groupId,
                    organizationId,
                    serviceGroupSettingId,
                    serviceId,
                    serviceOrganizationSettingId,
                })}
            >
                {translate.common.add}
            </LinkButton>,
        ];
    }

    return (
        <Loading
            item={compensationPayouts}
            loaded={(compensations) => (
                <>
                    <FormHeader actionButtons={actionButtons}>{translate.compensationPayouts.fees}</FormHeader>
                    <AdminTable>
                        <TableHead>
                            <TableRow>
                                <TableHeader>{translate.common.startDate}</TableHeader>
                                <TableHeader>{translate.common.endDate}</TableHeader>
                                <TableHeader>{translate.compensationPayouts.amount}</TableHeader>
                                <TableHeader>{translate.common.activity}</TableHeader>
                                <TableHeader></TableHeader>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {compensations.map((compensation, key) => (
                                <TableRow key={key}>
                                    <TableData>{DateTime.from(compensation.startDate).format('DD-MM-YYYY')}</TableData>
                                    <TableData>
                                        {compensation.endDate
                                            ? DateTime.from(compensation.endDate).format('DD-MM-YYYY')
                                            : ''}
                                    </TableData>
                                    <TableData>
                                        <FormatCurrency value={compensation.amount} />
                                    </TableData>
                                    <TableData>
                                        {
                                            serviceActivities.find(
                                                (serviceActivity) =>
                                                    serviceActivity.id === compensation.serviceActivityId
                                            )?.name
                                        }
                                    </TableData>
                                    {canEdit && compensation.id && (
                                        <TableData>
                                            <EditButton
                                                to={getUrlWithParams(
                                                    `/serviceActivityCompensationPayout/${compensation.id}`,
                                                    {
                                                        groupId,
                                                        organizationId,
                                                        serviceGroupSettingId,
                                                        serviceId,
                                                        serviceOrganizationSettingId,
                                                    }
                                                )}
                                            />
                                            <DeleteButton onClick={() => remove(compensation.id)} />
                                        </TableData>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </AdminTable>
                </>
            )}
        />
    );

    async function load() {
        if (serviceId) {
            const serviceActivitiesForService = await getServiceActivities(serviceId, abortSignal);
            setServiceActivities(serviceActivitiesForService);

            const compensationPayoutForService = await getServiceActivityCompensationPayouts(
                {
                    serviceGroupSettingId,
                    serviceOrganizationSettingId,
                },
                abortSignal
            );

            setCompensationPayouts(compensationPayoutForService);
        }
    }

    async function remove(id: string) {
        await deleteServiceActivityCompensationPayouts(id, abortSignal);
        await load();
    }
}
